import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';
import InputAdornment from '@material-ui/core/InputAdornment';
import Tooltip, { InfoPropTypes } from 'components/Tooltip';
import { useTranslation } from 'react-i18next';
import { PAGE_NAME } from 'utils/constants';
import COLORS from 'utils/constants/colors';

const useStyles = makeStyles(theme => ({
  input: {
    color: theme.palette.text.primary,
    width: '100%',
    background: theme.palette.background.default,
    border: `1px solid ${theme.palette.text.secondary}`,
  },
  rootInput: {
    background: ({ disabled }) => {
      if (typeof disabled === 'boolean' && disabled) {
        return `${theme.palette.text.disabled}!important`;
      } else {
        return 'none';
      }
    },
    color: ({ disabled }) => {
      if (typeof disabled === 'boolean' && disabled) {
        return theme.palette.secondary.light;
      } else {
        return theme.palette.text.primary;
      }
    },
    '&::placeholder': {
      color: ({ disabled }) => {
        if (typeof disabled === 'boolean' && disabled) {
          return theme.palette.secondary.light;
        } else {
          return COLORS.INPUT_GREY;
        }
      },
    },
  },
  disabled: {
    color: theme.palette.secondary.light,
    opacity: 0.2,
  },
  formControl: {
    display: 'flex',
    alignItems: 'baseline',
  },
  errorVisible: {
    opacity: 1,
  },
  errorHidden: {
    opacity: 0,
  },
  borderWithIcon: {
    border: `1px solid ${theme.palette.text.secondary}`,
    '& .MuiInput-input': {
      color: theme.palette.text.primary,
    },
    height: '32px',
  },

  endAdornmentWrapper: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    background: theme.palette.primary.main,
    '& [id="Layer_1"]': {
      color: `${theme.palette.secondary.light} !important`,
      '& [class*="st0"]': {
        fill: `${theme.palette.secondary.light} !important`,
      },
    },
  },
  inputAdornmentWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    background: theme.palette.primary.main,
    padding: '4px',
    height: 38,
    '& p': {
      color: theme.palette.secondary.light,
    },
  },
  loginInput: {
    borderBottom: `1px solid ${theme.palette.text.secondary}`,
    color: theme.palette.text.primary,
    width: '100%',
    height: '30px',
    // padding: '10px',
    marginRight: '15px',
  },
}));

export const STYLE_TYPE = {
  default: 'DEFAULT',
  iconBorder: 'ICON_BORDER',
  loginInput: 'LOGIN_INPUT',
};

const TextInput = ({
  id,
  type,
  label,
  className,
  required,
  error,
  onChange,
  value,
  onFocus,
  field,
  placeholder,
  endAdornment,
  disabled,
  maxLength,
  startAdornment,
  info,
  styleType = STYLE_TYPE.default,
  displayError = true,
  view,
}) => {
  const { t } = useTranslation();
  const classes = useStyles({ disabled });
  const onChangeInput = e => {
    if (type === 'number') {
      onChange(e.target.value);
      return;
    }
    onChange(e);
  };

  return (
    <FormControl
      className={cn(classes.formControl, className)}
      error={Boolean(error)}
    >
      {label && (
        <InputLabel
          shrink
          disableAnimation
          required={required}
          htmlFor="component-error"
          variant="standard"
          focused={false}
          classes={{ disabled: classes.disabled }}
        >
          {t(label)}
          {info && <Tooltip text={info} />}
        </InputLabel>
      )}
      <Input
        className={cn(
          { [classes.input]: styleType !== STYLE_TYPE.loginInput },
          { [classes.loginInput]: styleType === STYLE_TYPE.loginInput },
          { [classes.borderWithIcon]: styleType === STYLE_TYPE.iconBorder },
          className,
        )}
        disableUnderline
        name={field}
        type={type}
        onFocus={onFocus}
        id={id}
        classes={{
          input: classes.rootInput,
          // disabled: classes.disabled,
        }}
        value={value}
        onChange={onChangeInput}
        placeholder={t(placeholder)}
        aria-describedby="component-error-text"
        onKeyPress={e => {
          view === PAGE_NAME.MANAGERIAL &&
            e.key === 'Enter' &&
            e.preventDefault();
        }}
        endAdornment={
          styleType === STYLE_TYPE.iconBorder ? (
            <div className={classes.endAdornmentWrapper}>{endAdornment}</div>
          ) : (
            endAdornment
          )
        }
        disabled={disabled}
        inputProps={{ maxLength: maxLength, min: 0 }}
        startAdornment={
          startAdornment ? (
            <InputAdornment
              classes={{
                root: classes.inputAdornmentWrapper,
              }}
              position="start"
            >
              {startAdornment}
            </InputAdornment>
          ) : null
        }
        {...(styleType === STYLE_TYPE.iconBorder && {
          disableUnderline: true,
        })}
      />
      {displayError && error && (
        <FormHelperText
          className={error ? classes.errorVisible : classes.errorHidden}
          id="component-error-text"
        >
          {t(error)}
        </FormHelperText>
      )}
    </FormControl>
  );
};

TextInput.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  view: PropTypes.string,
  className: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onFocus: PropTypes.func,
  field: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  type: PropTypes.string,
  endAdornment: PropTypes.element,
  disabled: PropTypes.bool,
  maxLength: PropTypes.number,
  startAdornment: PropTypes.string,
  info: InfoPropTypes,
  styleType: PropTypes.oneOf(Object.values(STYLE_TYPE)),
  displayError: PropTypes.bool,
};

TextInput.defaultProps = {
  id: 'MuiInput',
  maxLength: 320,
};

export default TextInput;
